import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Divider,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import type { ArtifactInfo } from '../../agent'
import useDashboards from '../../hooks/useDashboards'
import useStoryBoardPublish, { storyTargetsKey } from '../../hooks/useStoryBoardPublish'
import { reportClientError } from '../../utils/reportClientError'
import { dashboardTypeLabel } from '../../utils/dashboardType'
import {
  DEFAULT_SHARE_ACCESS_LEVEL,
  ShareAccessFields,
  ShareLinkField,
  sharePasswordMissing,
} from '../dashboard/ShareControls'

interface PublishWebappDialogProps {
  open: boolean
  onClose: () => void
  customer: string
  defaultName: string
  defaultDescription: string
  /**
   * The output being published: a web app (a Web app or a Presentation) or
   * a video. When it is already
   * published to DataViz, the dialog opens on that item's name and share
   * settings, and Publish updates it.
   */
  artifact: ArtifactInfo | null
}

/**
 * Publishing an output (a web app or a video) puts it in DataViz, which is
 * also how it is shared (#1578 items 12, 13): one item per output, one link,
 * the same share controls as every other dashboard. Publishing again updates that dashboard,
 * whatever name it is given; the server decides, so this dialog only says
 * which it will be. The server also decides the type (a web app whose page is
 * a deck is a Presentation); the dialog names the one it was told.
 */
const PublishWebappDialog: React.FC<PublishWebappDialogProps> = ({
  open,
  onClose,
  customer,
  defaultName,
  defaultDescription,
  artifact,
}) => {
  const artifactId = artifact?.id
  const [name, setName] = useState(defaultName)
  const [description, setDescription] = useState(defaultDescription)
  const [accessLevel, setAccessLevel] = useState<string>(DEFAULT_SHARE_ACCESS_LEVEL)
  const [password, setPassword] = useState('')
  const [result, setResult] = useState<{ share_id: string; dashboard_id: string; type?: string; updated: boolean } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const { publishArtifact, publishArtifactIsLoading } = useDashboards({ customer })
  const { published, others, targetsLoading, datavizType } = useStoryBoardPublish(artifactId, open)
  const hasPassword = Boolean(published?.has_password && published.share_access_level === 'password')

  // Reset the form each time the dialog opens.
  const wasOpenRef = React.useRef(false)
  const prefilledRef = React.useRef<string | null>(null)
  if (wasOpenRef.current !== open) {
    wasOpenRef.current = open
    prefilledRef.current = null
    if (open) {
      setName(defaultName)
      setDescription(defaultDescription)
      setAccessLevel(DEFAULT_SHARE_ACCESS_LEVEL)
      setPassword('')
      setResult(null)
      setError(null)
    }
  }
  // Once the dashboard it will update is known, open on what is published
  // now. Only before a publish: the publish itself refreshes the targets, and
  // that must not wipe the result it is showing.
  if (open && published && !result && prefilledRef.current !== published.id) {
    prefilledRef.current = published.id
    setName(published.name || defaultName)
    setDescription(published.description || '')
    setAccessLevel(published.share_enabled && published.share_access_level ? published.share_access_level : DEFAULT_SHARE_ACCESS_LEVEL)
  }

  const handlePublish = async () => {
    setError(null)
    try {
      const res = await publishArtifact({
        artifactId: artifactId!,
        name: name.trim(),
        description: description.trim(),
        accessLevel,
        password: accessLevel === 'password' ? password : undefined,
      })
      setResult({ ...res, updated: Boolean(published) })
      if (artifactId) queryClient.invalidateQueries({ queryKey: storyTargetsKey(artifactId) })
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to publish')
      reportClientError({
        feature: 'publish-webapp',
        error: err,
        context: {
          artifact_id: artifactId,
          customer,
          access_level: accessLevel,
        },
      })
    }
  }

  const updating = Boolean(published)
  // "as a Presentation": what the server said it published, else what the
  // targets say the page will publish as. Nothing when neither is known.
  const shownType = result?.type || datavizType
  const asType = shownType ? ` as a ${dashboardTypeLabel(shownType)}` : ''

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{(result ? result.updated : updating) ? 'Update in DataViz' : `Publish to DataViz${asType}`}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {result ? (
            <>
              <Alert severity="success">
                {result.updated ? `Updated in DataViz${asType}. The link stays the same.` : `Published to DataViz${asType}.`}
              </Alert>
              <ShareLinkField shareId={result.share_id} />
            </>
          ) : (
            <>
              {updating && (
                <Typography data-testid="story-update-published" sx={{ fontSize: 14, color: 'text.secondary' }}>
                  Already in DataViz as “{published!.name}”. {datavizType === 'video' ? 'Publishing replaces its video with this one.' : 'Publishing updates it to the current draft.'}
                  {!published!.share_enabled && ' Sharing is off for it; updating turns it back on at the access level below.'}
                </Typography>
              )}
              {others.length > 0 && (
                <Typography data-testid="publish-others" sx={{ fontSize: 14, color: 'text.secondary' }}>
                  {others.map(o => `Also published by ${o.owner} as “${o.name}”.`).join(' ')}
                </Typography>
              )}
              <TextField
                fullWidth
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
              />
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={2}
              />

              <Divider sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Sharing
                </Typography>
              </Divider>

              <ShareAccessFields
                accessLevel={accessLevel}
                onAccessLevelChange={setAccessLevel}
                password={password}
                onPasswordChange={setPassword}
                hasPassword={hasPassword}
              />

              {error && <Alert severity="error">{error}</Alert>}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="info" onClick={onClose}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            onClick={handlePublish}
            variant="contained"
            color="info"
            disabled={
              !artifactId ||
              !name.trim() ||
              publishArtifactIsLoading ||
              targetsLoading ||
              sharePasswordMissing(accessLevel, password, hasPassword)
            }
            startIcon={publishArtifactIsLoading ? <CircularProgress size={16} /> : undefined}
          >
            {publishArtifactIsLoading ? 'Publishing…' : updating ? 'Update' : 'Publish'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default PublishWebappDialog
